import { HttpClient } from '@/shared/infra/http/HttpClient';
import { DuplicateCaseContinueRequest } from '../../application/domain/entities/DuplicateCaseContinueRequest';
import { DuplicateCaseLookupResult } from '../../application/domain/entities/DuplicateCaseLookupResult';
import { IDuplicateCaseRepository } from '../../application/ports/outbound/IDuplicateCaseRepository';
import { DuplicateCaseLookupResponseDto } from './dto/DuplicateCaseLookupResponseDto';
import { mapDuplicateCaseLookupResponseDtoToEntity } from './mappers/duplicateCaseLookupMapper';

export class ApiDuplicateCaseRepository implements IDuplicateCaseRepository {
  constructor(private readonly httpClient: HttpClient) { }

  async fetchDuplicateCaseLookups(
    everifyCaseDuplicateId: string
  ): Promise<DuplicateCaseLookupResult> {
    try {
      const url = `/e-verify/duplicate-cases/${everifyCaseDuplicateId}`;
      const response = await this.httpClient.get<DuplicateCaseLookupResponseDto>(url);
      return mapDuplicateCaseLookupResponseDtoToEntity(response.data);
    } catch (error) {
      console.error('Error fetching duplicate case lookups:', error);
      throw error;
    }
  }

  async continueCreateDuplicateCase(
    everifyCaseDuplicateId: string,
    request: DuplicateCaseContinueRequest
  ): Promise<void> {
    await this.httpClient.post(
      `/e-verify/duplicate-cases/${everifyCaseDuplicateId}/continue-create`,
      request
    );
  }

  async abandonDuplicateCase(everifyCaseDuplicateId: string, reason: string): Promise<void> {
    // `abandonReason` is required by the API and is a local field — never sent to E-Verify — that
    // records why no case was filed. The continue reason codes cannot carry it: all of them
    // describe a reason to proceed, and this is the decision not to. The endpoint returns the
    // record's terminal state, which the UI does not need, so success here is just "the POST landed".
    await this.httpClient.post(`/e-verify/duplicate-cases/${everifyCaseDuplicateId}/abandon`, {
      abandonReason: reason,
    });
  }

  async linkExistingCase(
    everifyCaseDuplicateId: string,
    everifyDuplicateCaseLookupId: string
  ): Promise<void> {
    // Says WHICH reported case is this employee's: the duplicate check answers for the whole
    // company, and guessing would attach a real DHS case to the wrong I-9.
    await this.httpClient.post(`/e-verify/duplicate-cases/${everifyCaseDuplicateId}/link`, {
      everifyDuplicateCaseLookupId,
    });
  }
}
